import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Smartphone, Download, X, CheckCircle2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { isStandalone, isIOS, canPromptInstall, onInstallAvailability, promptInstall } from "@/lib/install";

const STEPS = {
  es: {
    ios: ["Abre faithcycle en Safari (no funciona desde Chrome en iPhone).", "Toca el botón Compartir (el cuadro con la flecha hacia arriba) abajo de la pantalla.", "Baja y toca \"Agregar a pantalla de inicio\".", "Toca \"Agregar\". Listo: verás el ícono de FaithCycle junto a tus apps."],
    android: ["Abre faithcycle en Chrome.", "Toca el menú de los tres puntos (⋮) arriba a la derecha.", "Toca \"Instalar app\" o \"Agregar a pantalla principal\".", "Confirma con \"Instalar\". El ícono aparecerá junto a tus apps."],
  },
  en: {
    ios: ["Open faithcycle in Safari (Chrome on iPhone can't install apps).", "Tap the Share button (square with an up arrow) at the bottom.", "Scroll down and tap \"Add to Home Screen\".", "Tap \"Add\". Done: the FaithCycle icon appears with your apps."],
    android: ["Open faithcycle in Chrome.", "Tap the three-dot menu (⋮) at the top right.", "Tap \"Install app\" or \"Add to Home screen\".", "Confirm with \"Install\". The icon appears with your apps."],
  },
};

function useCanInstall() {
  const [can, setCan] = useState(canPromptInstall());
  useEffect(() => onInstallAvailability(setCan), []);
  return can;
}

function StepList({ title, steps, testid }) {
  return (
    <div data-testid={testid}>
      <p className="font-serif text-lg mb-3">{title}</p>
      <ol className="space-y-2">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-3 text-sm leading-relaxed">
            <span className="w-6 h-6 rounded-full bg-[var(--highlight)]/60 text-[var(--accent)] text-xs flex items-center justify-center flex-shrink-0">{i + 1}</span>
            <span>{s}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function InstallGuide() {
  const { lang } = useApp();
  const es = lang === "es";
  const can = useCanInstall();
  const steps = STEPS[es ? "es" : "en"];
  const ios = isIOS();
  const iosBlock = <StepList title="iPhone / iPad" steps={steps.ios} testid="install-steps-ios" />;
  const androidBlock = <StepList title="Android" steps={steps.android} testid="install-steps-android" />;

  return (
    <div id="install" className="mt-6 rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-6 scroll-mt-24" data-testid="install-guide-card">
      <div className="flex items-center gap-2 mb-1">
        <Smartphone className="w-4 h-4 text-[var(--accent)]" strokeWidth={1.5} />
        <h3 className="font-serif text-xl tracking-tight">{es ? "Instala FaithCycle en tu teléfono" : "Install FaithCycle on your phone"}</h3>
      </div>
      <p className="text-xs text-[var(--text-secondary)] mb-5">
        {es
          ? "Una vez instalada, la abres desde su ícono como cualquier app, sin tener que entrar al navegador ni escribir la dirección. No necesitas tienda de apps."
          : "Once installed, open it from its icon like any app, without opening the browser or typing the address. No app store needed."}
      </p>
      {isStandalone() ? (
        <p className="text-sm flex items-center gap-2" data-testid="install-already-installed">
          <CheckCircle2 className="w-4 h-4 text-[var(--accent)]" strokeWidth={1.5} />
          {es ? "Ya estás usando la app instalada." : "You're already using the installed app."}
        </p>
      ) : (
        <div className="space-y-6">
          {can && (
            <button data-testid="install-now-btn" onClick={promptInstall} className="rounded-full h-11 px-5 bg-[var(--accent)] text-white flex items-center gap-2 hover:opacity-90 transition-opacity">
              <Download className="w-4 h-4" strokeWidth={1.5} /> {es ? "Instalar ahora" : "Install now"}
            </button>
          )}
          {ios ? <>{iosBlock}{androidBlock}</> : <>{androidBlock}{iosBlock}</>}
        </div>
      )}
    </div>
  );
}

export function InstallBanner() {
  const { lang } = useApp();
  const es = lang === "es";
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(() => isStandalone() || localStorage.getItem("fc_install_banner_dismissed") === "1");
  if (hidden) return null;
  const dismiss = (e) => {
    e.stopPropagation();
    localStorage.setItem("fc_install_banner_dismissed", "1");
    setHidden(true);
  };
  return (
    <div
      role="button"
      data-testid="install-banner"
      onClick={() => navigate("/profile#install")}
      className="rounded-2xl border border-[var(--accent)]/40 bg-[var(--highlight)]/40 p-4 flex items-center gap-3 cursor-pointer hover:bg-[var(--highlight)]/60 transition-colors"
    >
      <Smartphone className="w-5 h-5 text-[var(--accent)] flex-shrink-0" strokeWidth={1.5} />
      <p className="text-sm flex-1">
        {es ? "Instala FaithCycle en tu teléfono y ábrela como app, sin navegador. " : "Install FaithCycle on your phone and open it like an app, no browser. "}
        <span className="text-[var(--accent)] underline underline-offset-2">{es ? "Ver pasos" : "See steps"}</span>
      </p>
      <button data-testid="install-banner-dismiss" onClick={dismiss} aria-label="close" className="p-1 rounded-full hover:bg-[var(--surface)] transition-colors">
        <X className="w-4 h-4 text-[var(--text-secondary)]" strokeWidth={1.5} />
      </button>
    </div>
  );
}
